import { Inbox } from "lucide-react";
import { useGetList } from "ra-core";
import { Link } from "react-router";

import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import type { FormSubmission, LeadForm } from "./formModel";

/** Read-only capture log for one form (append-only server-side). */
export const FormSubmissionsPanel = ({ form }: { form: LeadForm }) => {
  const { data: submissions } = useGetList<FormSubmission>("form_submissions", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: { form_id: form.id },
  });

  return (
    <SectionCard
      title="Submissions"
      subtitle="Latest 50, each one is linked to its contact record"
    >
      {(submissions ?? []).length === 0 ? (
        <div className="py-6">
          <EmptyState
            icon={Inbox}
            title="No submissions yet"
            description="Share the public link, every submission lands here with its attribution."
          />
        </div>
      ) : (
        <div className="flex flex-col">
          {(submissions ?? []).map((submission) => {
            const summary = Object.entries(submission.data)
              .filter(([, value]) => value && value !== "true")
              .slice(0, 4)
              .map(([, value]) => value)
              .join(" · ");
            const source =
              submission.utm.utm_source ??
              (submission.utm.utm_campaign
                ? `campaign ${submission.utm.utm_campaign}`
                : null);
            return (
              <div
                key={submission.id}
                className="flex items-center justify-between gap-3 border-b border-border-subtle py-2.5 text-[13px] last:border-0"
              >
                <div className="min-w-0">
                  <p className="truncate text-foreground">{summary || ", "}</p>
                  <p className="text-[12px] text-muted-foreground">
                    {new Date(submission.created_at).toLocaleString()}
                    {source ? ` · via ${source}` : ""}
                  </p>
                </div>
                {submission.contact_id ? (
                  <Link
                    className="shrink-0 text-[12.5px] font-medium text-primary no-underline hover:underline"
                    to={`/contacts/${submission.contact_id}/show`}
                  >
                    View contact
                  </Link>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};
